import { useTranslator } from "@eo-locale/react";

import { has } from "lodash";

import { useState, useEffect } from "react";

import { useRouter } from "next/router";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  Contribution,
  FiAlertTriangle,
  FiTrash,
  VStack,
  FiEdit2,
  useDisclosure
} from "@/storybook-components";
import { buildImgixUrl, dateWithSiteLocale, stripEditorHtml } from "@/lib";
import { usePrivateFileRecordImageUrl } from "@/hooks/use-private-file-display-url";
import Comments from "../../common/comments";
import AddCommentButton from "../../common/add-comment-button";
import ActionList, { type MenuItemConfig } from "../../common/action-list";
import {
  fetchComments,
  fetchMentionSuggestions,
  reportIdea,
  reportIdeaComment,
  removeIdeaComment,
  refreshOneIdea,
  removeIdea,
  fetchUserBudgetAllocations
} from "@/redux/features/brainstormers/thunk";
import {
  setCloseIdeaStatus,
  removeIdeaFromList
} from "@/redux/features/brainstormers/slice";

import useUser from "@/hooks/useUser";
import { getVotingStatus } from "@/helpers/ideas";
import { categoryMap } from "@/helpers/categories";
import OpenPostDrawer from "../../actions/open-post-drawer";
import useBudget from "@/hooks/useBudget";
import useCanCreatePost from "@/hooks/useCanCreatePost";

const Idea = ({
  manageVotes,
  manageBudgeting,
  onContributionDrawerClose
}): JSX.Element => {
  const { translate, language } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const routerPath = router.asPath;

  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { project } = useAppSelector((state) => state.project);
  const {
    idea,
    status,
    brainstormer,
    comments,
    mentions,
    currentBAContributionId
  } = useAppSelector((state) => state.brainstormers);
  const [ideaDetails, setIdeaDetails] = useState(idea);
  const { moderated } = useAppSelector((state) => state?.site?.site);
  const { login: profileUser, type: profileUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );
  const currentIdeaId = idea?.id;
  const {
    allowCommentsForIdeas,
    state: brainstormerState,
    votingStartAt,
    votingEndAt,
    disableVoting,
    layout
  } = brainstormer;
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { isUserAdmin, isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } =
    useUser();
  const { unverifiedUser, registeredUser } = useAppSelector(
    (state) => state.user
  );
  const registeredUserLogin =
    isRegisteredUserLoggedIn && registeredUser?.profile.login;
  const unverifiedUserLogin = isRegisteredUserLoggedIn && unverifiedUser?.login;
  const loggedInUser = registeredUserLogin || unverifiedUserLogin;
  const { state: projectState, visibilityMode, canContribute } = project || {};

  const { formatBudget } = useBudget();
  const canCreatePost = useCanCreatePost();

  const editPostDisclosure = useDisclosure();
  const [isEditTriggered, setIsEditTriggered] = useState(false);
  const [currentPostData, setCurrentPostData] = useState(null);

  const { projectId, brainstormerId } = router.query as {
    projectId: string;
    brainstormerId: string;
  };

  const categories = useAppSelector((state) => state.brainstormers.categories);
  const catMap = categoryMap(categories);

  useEffect(() => {
    if (status === "ideaUpdated") {
      dispatch(refreshOneIdea({ ideaId: currentIdeaId }));
    }
  }, [status]);

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const allowCommenting = () => {
    if (!allowCommentsForIdeas) {
      return false;
    }
    if (
      isUserAdmin &&
      !(brainstormerState === "archived" || projectState === "archived")
    ) {
      return true;
    }

    if (
      allowCommentsForIdeas &&
      projectState === "published" &&
      brainstormerState === "published"
    ) {
      return true;
    }

    return false;
  };

  const commentingDisabled =
    (visibilityMode === "Panelled" || visibilityMode === "Protected") &&
    !canContribute;

  const { comment } = router.query;

  const [showCommentBox, setShowCommentBox] = useState<boolean>(
    comment &&
      !commentingDisabled &&
      allowCommenting() &&
      (isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn)
  );

  /** Opens the composer when URL gains `comment=true` (post-auth cookie resume). */
  useEffect(() => {
    if (router.query.comment !== "true") return;
    if (commentingDisabled || !allowCommenting()) return;
    if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) return;

    setShowCommentBox(true);
  }, [
    router.query.comment,
    commentingDisabled,
    allowCommentsForIdeas,
    brainstormerState,
    projectState,
    isUserAdmin,
    isRegisteredUserLoggedIn,
    isUnverfiedUserLoggedIn
  ]);

  useEffect(() => {
    if (currentIdeaId) {
      dispatch(
        fetchMentionSuggestions({
          projectId,
          brainstormerId,
          ideaId: currentIdeaId
        })
      );
      dispatch(
        fetchComments({
          projectId,
          brainstormerId,
          ideaId: currentIdeaId
        })
      );
    }
  }, [brainstormerId, currentIdeaId, dispatch, projectId]);

  useEffect(() => {
    setIdeaDetails(idea);
  }, [idea]);

  const actionButtonVisibility =
    projectState === "archived" ||
    brainstormerState === "archived" ||
    isVotingAllowed ||
    !(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn);

  const isActionButtonVisible =
    (visibilityMode === "Panelled" || visibilityMode === "Protected") &&
    !canContribute;

  const handleDeleteClick = async (currentData) => {
    const { id } = currentData;
    if (has(currentData, "comment")) {
      dispatch(
        removeIdeaComment({
          projectId,
          brainstormerId,
          ideaId: currentIdeaId,
          commentId: id
        })
      );
    } else {
      await dispatch(
        removeIdea({
          projectId,
          brainstormerId,
          ideaId: currentIdeaId
        })
      );
      // Remove from local state immediately
      dispatch(removeIdeaFromList(currentIdeaId));
      if (layout === "BudgetAllocator")
        await dispatch(fetchUserBudgetAllocations());
      onContributionDrawerClose();
    }
  };

  const handleEditClick = async (currentData) => {
    await setIsEditTriggered(true);
    await setCurrentPostData(currentData);
    await dispatch(setCloseIdeaStatus("editTriggered"));
    onContributionDrawerClose();

    const [updatedPath] = routerPath.split("?");
    router.replace(updatedPath, undefined, { shallow: true });
  };

  const menuList: MenuItemConfig[] = [
    {
      icon: <FiAlertTriangle />,
      title: translate("brainstormers.reportToModerator"),
      type: "report",
      isItemVisible: moderated,
      actionHandler: null,
      hasAlert: true,
      loggedInUser: loggedInUser,
      alertContent: {
        header: translate("brainstormers.youWantToReportContent"),
        body: translate("brainstormers.reportContentDesc"),
        buttonPrimary: translate("common.yes"),
        buttonSecondary: translate("common.no"),
        buttonPrimaryVariant: "primary",
        primaryDestructive: false,
        buttonSecondaryVariant: "secondary",
        secondaryDestructive: false,
        onPrimaryClickHandler: (currentData) => {
          const ideaId = idea.id;
          if (has(currentData, "comment")) {
            const commentId = currentData.id;
            dispatch(
              reportIdeaComment({
                ideaId,
                commentId
              })
            );
          } else {
            dispatch(
              reportIdea({
                ideaId
              })
            );
          }
        }
      }
    },
    {
      icon: <FiEdit2 />,
      title: translate("common.edit"),
      type: "edit",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      actionHandler: handleEditClick,
      hasAlert: false,
      loggedInUser: loggedInUser,
      alertContent: null,
      disabled: isActionButtonVisible
    },
    {
      icon: <FiTrash />,
      title: translate("common.delete"),
      type: "delete",
      isItemVisible: !actionButtonVisibility && !isActionButtonVisible,
      hasAlert: true,
      loggedInUser,
      menuItemColor: "red.600",
      alertContent: {
        header: translate("brainstormers.ideaDeleteContentTitle"),
        body: translate("brainstormers.ideaDeleteContentDescription"),
        buttonPrimary: translate("common.delete"),
        buttonSecondary: translate("common.cancel"),
        buttonPrimaryVariant: "primary",
        primaryDestructive: true,
        buttonSecondaryVariant: "secondary",
        secondaryDestructive: false,
        onPrimaryClickHandler: handleDeleteClick
      }
    }
  ];

  const commentNode = () => {
    if (allowCommentsForIdeas) {
      return (
        <Comments
          menuList={menuList}
          status={status}
          comments={comments}
          showCommentBox={showCommentBox}
          setShowCommentBox={setShowCommentBox}
          mentions={mentions}
          commentable={allowCommenting()}
          commentingDisabled={commentingDisabled}
        />
      );
    }
  };
  const {
    id,
    description,
    name,
    createdAt,
    author,
    avatar,
    currentUserVoted,
    votesCount = 0,
    commentsCount = 0,
    backgroundColourId,
    userType,
    baItemConfig,
    currentUserAllocation,
    categoryId
  } = ideaDetails ?? {};
  const { unitCap } = baItemConfig || {};
  const unitCode = brainstormer?.baCategoryConfigAttributes?.unitCode || "";

  const categoryName =
    categoryId != null ? catMap[String(categoryId)] : undefined;

  const totalFund = formatBudget(
    currentUserAllocation || unitCap || 0,
    unitCode
  );

  const isLoggedInUser = author === profileUser;
  const isMatchingUserType = userType === profileUserType;
  const hasActionOptions =
    moderated ||
    (isLoggedInUser &&
      isMatchingUserType &&
      !isVotingAllowed &&
      projectState !== "archived" &&
      brainstormerState !== "archived");

  const mediaImageUrl = usePrivateFileRecordImageUrl({
    record: ideaDetails,
    tool: "ideas",
    height: "auto",
    width: "auto",
    fit: "min"
  });

  return (
    <VStack gap={8} px={4} data-testid="ideas-detail">
      <Contribution
        showFullTextContent
        hideHeader={!canCreatePost}
        id="budget-contribution-card"
        cardDescription={stripEditorHtml(description) || ""}
        cardHeading={name}
        variant="vertical"
        showCommentIcon={allowCommentsForIdeas}
        commentsCount={commentsCount}
        contributionTime={dateWithSiteLocale(createdAt, language.toLowerCase())}
        imageAlt={name}
        imageSrc={mediaImageUrl}
        isThumbsUp={!disableVoting}
        shareText={translate("brainstormers.share")}
        showDescription
        showFooter
        showImage
        thumbsUpCount={votesCount}
        handleVoteUp={() => manageVotes(currentIdeaId)}
        isLoaded={status !== "ideaFetching"}
        userAvatarSrc={buildImgixUrl(avatar, 40, 40, "min")}
        tileBackgroundColor={
          backgroundColourId
            ? backgroundColours.find(
                ({ id }) => `${backgroundColourId}` === `${id}`
              )?.name
            : "gray.100"
        }
        showTags={!!categoryName}
        tags={categoryName ? [categoryName] : []}
        username={author}
        showMoreOptions={hasActionOptions}
        actionList={
          <ActionList menuList={menuList} currentData={ideaDetails} />
        }
        isVoteActive={currentUserVoted}
        ariaLabelVoteIcon={`${translate("brainstormers.numberOfVotes", {
          votesCount
        })} ${
          ideaDetails?.currentUserVoted
            ? translate("brainstormers.removeVote")
            : translate("brainstormers.addVote")
        }`}
        ariaLabelCommentIcon={`${translate("brainstormers.numberOfComments", {
          commentsCount
        })} ${translate("brainstormers.addComment")}`}
        isIconButtonPressed={currentUserVoted}
        showShareText
        isShareIcon={!settings.disableSocialMediaSharing}
        ariaLabelShareIcon={translate("brainstormers.share")}
        socialShareTitle={name || ""}
        socialShareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}?idea=${id}`}
        showBudgeting={layout === "BudgetAllocator"}
        totalFund={totalFund}
        fundLocale={translate("budget.fundFor")}
        fundedForLocale={translate("budget.fundedFor")}
        undoBudgetingLocale={translate("budget.undo")}
        hasTotalFunded={!!currentUserAllocation}
        handleBudgeting={() => manageBudgeting(id)}
        isBudgetUpdating={
          currentBAContributionId === id && status === "updatingUserAllocation"
        }
      />
      {allowCommenting() && !showCommentBox && (
        <AddCommentButton
          disabled={commentingDisabled}
          onClick={() => setShowCommentBox(true)}
        />
      )}
      {commentNode()}
      {(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn) &&
        isEditTriggered && (
          <OpenPostDrawer
            onOpen={editPostDisclosure.onOpen}
            open={editPostDisclosure.open}
            onClose={editPostDisclosure.onClose}
            type="edit"
            isEditPost={isEditTriggered}
            editHandler={setIsEditTriggered}
            currentPostData={currentPostData}
          />
        )}
    </VStack>
  );
};

export default Idea;
